// Multi-select helpers for the Konva timeline. The currently selected event
// indices live on `eventGroupState[primary].selected`; this module is pure and
// just computes the next selection array given a click + modifiers.

/**
 * Apply a region click to the current selection following the same modifier
 * conventions as the legacy WaveSurfer timeline:
 *   - Ctrl/Cmd: toggle the clicked event in/out of the selection
 *   - Shift: select the range from `selected[0]` to the clicked event
 *   - no modifier: single-select the clicked event
 *
 * @param {object} input
 * @param {number} input.eventIndex
 * @param {{ ctrlKey?: boolean, metaKey?: boolean, shiftKey?: boolean }} input.modifiers
 * @param {number[]} input.currentSelected
 * @returns {number[]}
 */
export function applyRegionClick({ eventIndex, modifiers, currentSelected }) {
    const current = Array.isArray(currentSelected) ? currentSelected : [];
    const ctrlOrMeta = Boolean(modifiers?.ctrlKey || modifiers?.metaKey);
    const shift = Boolean(modifiers?.shiftKey);

    if (ctrlOrMeta) {
        if (current.includes(eventIndex)) {
            return current.filter((idx) => idx !== eventIndex);
        }
        return [...current, eventIndex];
    }

    if (shift && current.length > 0) {
        const anchor = current[0];
        const lo = Math.min(anchor, eventIndex);
        const hi = Math.max(anchor, eventIndex);
        const range = [];
        for (let i = lo; i <= hi; i += 1) range.push(i);
        // Deduplicate while preserving order.
        return Array.from(new Set(range));
    }

    return [eventIndex];
}

/**
 * Dispatch the legacy custom event so the editor's virtual event list scrolls
 * to a clicked region (preserves the existing contract used by Editor.svelte).
 *
 * @param {number} eventIndex
 */
export function dispatchScrollToEvent(eventIndex) {
    if (typeof window === "undefined") return;
    try {
        window.dispatchEvent(
            new CustomEvent("scrollToVirtualListItem", { detail: { index: eventIndex } })
        );
    } catch {
        // CustomEvent unavailable - safe to ignore in non-browser contexts.
    }
}
